// ============================================================================
// @torre/expenses — Cuentas bancarias y tarjetas (#952, épica #959)
// ============================================================================
// Catálogo de las cuentas de la empresa (cheques/débito, tarjetas de crédito,
// otras) y la cobertura de sus estados de cuenta: qué meses de qué cuenta ya
// están cargados, cuáles faltan y cuáles están a medias.
//
// Sin I/O. OJO: este módulo se evalúa ANTES que `index.ts` (lo re-exporta al
// final), así que en el nivel superior no se puede usar nada importado de
// `./index`; solo dentro de funciones.
// ============================================================================

import { isIsoDate, monthRange, normalizeText, vendorSimilarity, type ValidationResult } from "./index";
import type { StatementTemplate } from "./statements";

export const EXPENSE_ACCOUNTS_COLLECTION = "expense_accounts";

/** `cheques` incluye débito. */
export const ACCOUNT_TYPES = ["cheques", "credito", "otra"] as const;
export type AccountType = (typeof ACCOUNT_TYPES)[number];

export const ACCOUNT_TYPE_LABELS: Record<AccountType, { es: string; en: string }> = {
  cheques: { es: "Cheques / débito", en: "Checking / debit" },
  credito: { es: "Tarjeta de crédito", en: "Credit card" },
  otra: { es: "Otra", en: "Other" },
};

export interface ExpenseAccount {
  id: string;
  /** Banco o emisor ("BBVA", "American Express"). */
  bank: string;
  /** Cómo la llama la empresa ("Tarjeta empresarial"). */
  alias: string;
  type: AccountType;
  /** Últimos 4 dígitos de la cuenta o tarjeta. */
  last4?: string | null;
  currency: string;
  /** Titular de la cuenta. */
  holder?: string | null;
  active: boolean;
  notes?: string | null;
  /** Mapeo de columnas guardado para sus CSV/Excel (#953). */
  importTemplate?: StatementTemplate | null;
  createdAt?: string;
}

export type ExpenseAccountInput = Partial<Omit<ExpenseAccount, "id">>;

export function validateAccount(input: ExpenseAccountInput): ValidationResult {
  const errors: string[] = [];
  if (!String(input.bank ?? "").trim()) errors.push("bank: requerido");
  if (!String(input.alias ?? "").trim()) errors.push("alias: requerido");
  if (!ACCOUNT_TYPES.includes(input.type as AccountType)) errors.push("type: cheques, credito u otra");
  if (input.last4 != null && input.last4 !== "" && !/^\d{4}$/.test(String(input.last4))) errors.push("last4: 4 dígitos");
  if (input.currency !== undefined && !/^[A-Z]{3}$/.test(String(input.currency))) errors.push("currency: código ISO de 3 letras");
  if (input.importTemplate != null && typeof input.importTemplate !== "object") errors.push("importTemplate: inválido");
  return { valid: errors.length === 0, errors };
}

/** "BBVA · Tarjeta empresarial ·1234" */
export function accountLabel(a: Pick<ExpenseAccount, "bank" | "alias" | "last4"> | null | undefined): string {
  if (!a) return "—";
  return [a.bank, a.alias].filter(Boolean).join(" · ") + (a.last4 ? ` ·${a.last4}` : "");
}

/**
 * Propone la cuenta de un estado de cuenta a partir del banco que lo emite y
 * los últimos 4 dígitos que trae. Solo propone cuando hay UNA candidata clara:
 * últimos 4 iguales (y banco compatible si se conoce) o, sin dígitos, el único
 * producto activo de ese banco.
 */
export function suggestAccount(
  accounts: readonly ExpenseAccount[],
  hint: { issuer?: string | null; last4?: readonly (string | null | undefined)[] | string | null },
): string | null {
  const active = accounts.filter((a) => a.active !== false);
  const digits = (Array.isArray(hint.last4) ? hint.last4 : [hint.last4]).filter((d): d is string => typeof d === "string" && /^\d{4}$/.test(d));
  const bankOk = (a: ExpenseAccount) => !hint.issuer || sameBank(a.bank, hint.issuer);
  if (digits.length) {
    const byDigits = active.filter((a) => a.last4 && digits.includes(a.last4));
    const both = byDigits.filter(bankOk);
    if (both.length === 1) return both[0].id;
    if (byDigits.length === 1 && !hint.issuer) return byDigits[0].id;
    if (byDigits.length) return null;
  }
  if (hint.issuer) {
    const byBank = active.filter((a) => sameBank(a.bank, hint.issuer as string));
    if (byBank.length === 1) return byBank[0].id;
  }
  return null;
}

function sameBank(a: string, b: string): boolean {
  const na = normalizeText(a);
  const nb = normalizeText(b);
  if (!na || !nb) return false;
  if (na === nb || na.includes(nb) || nb.includes(na)) return true;
  return vendorSimilarity(a, b) >= 1;
}

// --- Periodos de estado de cuenta ------------------------------------------------
export interface StatementPeriod {
  from: string | null;
  to: string | null;
}

export const isCompletePeriod = (p: StatementPeriod | null | undefined): p is { from: string; to: string } =>
  Boolean(p && isIsoDate(p.from) && isIsoDate(p.to) && (p.from as string) <= (p.to as string));

/** Importación mínima para cobertura y choque de periodos. */
export interface StatementImportLike {
  id: string;
  status?: string;
  accountId?: string | null;
  statementPeriod?: StatementPeriod | null;
}

export interface PeriodClash {
  /** Importación CONFIRMADA de la misma cuenta y el mismo periodo: bloquea. */
  exact: string | null;
  /** Otras importaciones de la cuenta cuyo periodo se encima: solo aviso. */
  overlaps: string[];
}

/**
 * ¿Ya se importó este estado de cuenta? Misma cuenta + mismo periodo en una
 * importación confirmada = el mismo documento. Un periodo que solo se encima
 * (un corte distinto) se avisa pero no se bloquea.
 */
export function findPeriodClash(
  imports: readonly StatementImportLike[],
  accountId: string | null | undefined,
  period: StatementPeriod | null | undefined,
  excludeId?: string | null,
): PeriodClash {
  const out: PeriodClash = { exact: null, overlaps: [] };
  if (!accountId || !isCompletePeriod(period)) return out;
  for (const im of imports) {
    if (im.id === excludeId || im.accountId !== accountId || !isCompletePeriod(im.statementPeriod)) continue;
    const p = im.statementPeriod;
    const same = p.from === period.from && p.to === period.to;
    if (same && im.status === "confirmada") {
      if (!out.exact) out.exact = im.id;
      continue;
    }
    if (p.from <= period.to && period.from <= p.to) out.overlaps.push(im.id);
  }
  return out;
}

// --- Cobertura cuenta × mes ----------------------------------------------------------
export const COVERAGE_STATUSES = ["cargado", "parcial", "faltante", "futuro"] as const;
export type CoverageStatus = (typeof COVERAGE_STATUSES)[number];

export interface CoverageCell {
  period: string;
  status: CoverageStatus;
  /** Días del mes cubiertos por algún estado de cuenta confirmado. */
  coveredDays: number;
  /** Días que se esperaba cubrir (el mes en curso solo cuenta hasta hoy). */
  expectedDays: number;
  importIds: string[];
}

export interface CoverageRow {
  accountId: string;
  label: string;
  active: boolean;
  months: CoverageCell[];
}

const DAY_MS = 86_400_000;
const dayNumber = (iso: string) => Math.floor(Date.parse(`${iso}T00:00:00Z`) / DAY_MS);
const daysInMonth = (period: string) => {
  const [y, m] = period.split("-").map(Number);
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
};

/**
 * Una fila por cuenta y una columna por mes. Un mes está «cargado» si los
 * estados de cuenta CONFIRMADOS cubren todos sus días (los cortes a media
 * quincena se suman entre sí), «parcial» si cubren algunos y «faltante» si
 * ninguno. Los meses posteriores a `today` son «futuro»; el mes en curso solo
 * se exige hasta hoy.
 */
export function accountCoverage(
  accounts: readonly ExpenseAccount[],
  imports: readonly StatementImportLike[],
  from: string,
  to: string,
  today?: string,
): CoverageRow[] {
  const months = monthRange(from, to);
  const todayMonth = today ? today.slice(0, 7) : null;
  return accounts.map((a) => {
    const own = imports.filter((im) => im.accountId === a.id && im.status === "confirmada" && isCompletePeriod(im.statementPeriod));
    const cells = months.map((period): CoverageCell => {
      if (todayMonth && period > todayMonth) return { period, status: "futuro", coveredDays: 0, expectedDays: 0, importIds: [] };
      const first = dayNumber(`${period}-01`);
      const total = daysInMonth(period);
      const expectedDays = todayMonth && period === todayMonth && today ? Number(today.slice(8, 10)) : total;
      const covered = new Set<number>();
      const ids: string[] = [];
      for (const im of own) {
        const p = im.statementPeriod as { from: string; to: string };
        const s = Math.max(dayNumber(p.from), first);
        const e = Math.min(dayNumber(p.to), first + expectedDays - 1);
        if (s > e) continue;
        ids.push(im.id);
        for (let d = s; d <= e; d += 1) covered.add(d);
      }
      const coveredDays = covered.size;
      const status: CoverageStatus = coveredDays === 0 ? "faltante" : coveredDays >= expectedDays ? "cargado" : "parcial";
      return { period, status, coveredDays, expectedDays, importIds: ids };
    });
    return { accountId: a.id, label: accountLabel(a), active: a.active !== false, months: cells };
  });
}

// --- Contrato HTTP ---------------------------------------------------------------------
export const STATEMENT_ENDPOINTS = {
  accountList: "/expensesAccountList",
  account: "/expensesAccount",
  coverage: "/expensesCoverage",
  statementImport: "/expensesStatementImport",
  importMeta: "/expensesImportMeta",
  refundLink: "/expensesRefundLink",
} as const;
